import React, { useState, useMemo } from 'react';
import { Box, Pagination, Stack, TextInput, ScrollArea, LoadingOverlay } from '@mantine/core';
import PropTypes from 'prop-types';
import './PaginateSelect.css';

// Rectangular box with a searchable select on top and pagination at bottom (now inline list instead of dropdown)
const PaginateSelect = ({
  data = [],
  value,
  onChange = () => {},
  placeholder = 'Select option',
  searchable = true,
  nothingFound = 'No options',
  radius = 'sm',
  shadow = 'sm',
  withAsterisk = false,
  page, // now optional controlled page
  defaultPage = 1, // NEW: for uncontrolled mode
  total,
  onPageChange = () => {},
  size = 'md',
  selectProps = {},
  paginationProps = {},
  searchValue: controlledSearch,
  onSearchChange,
  style,
  pageSize = 10,
  listHeight = 220,
  renderItem,
  colorScheme, // NEW: 'light' | 'dark' to force mode
  theme = 'dark', // 'light' or 'dark'
  expandOnSelect = false, // NEW: only expand options on select/click
  ...rest
}) => {
  // Pagination control (controlled vs uncontrolled)
  const isPageControlled = page !== undefined;
  const [internalPage, setInternalPage] = useState(defaultPage);
  const currentPage = isPageControlled ? page : internalPage;
  // Fix: Always call onPageChange in controlled mode (let parent handle duplicate state)
  const setPageUnified = (next) => {
    if (isPageControlled) {
      onPageChange(next);
    } else {
      setInternalPage(next);
      if (onPageChange) onPageChange(next);
    }
  };

  // Detect external pagination scenario: caller passes only one page worth of data but supplies total pages (>1)
  const externallyPaginated = useMemo(
    () => !!total && total > 1 ,
    [total, data.length, pageSize]
  );

  const computedTotal = useMemo(
    () => Math.max(1, Math.ceil(data.length / pageSize)),
    [data.length, pageSize]
  );
  const effectiveTotal = externallyPaginated ? total : (total || computedTotal);

  // Internal slice only when not externally paginated
  // Fix: define 'start' before use
  const start = (currentPage - 1) * pageSize;
  // In externally paginated mode, do not filter by searchValue, just show API data
  const currentPageItems = externallyPaginated
    ? data // Use data as-is for externally paginated mode
    : data.slice(start, start + pageSize);

  // Remove auto-clamping of page in controlled/external pagination mode
  React.useEffect(() => {
    if (externallyPaginated || isPageControlled) return; // do not auto-change page in external or controlled mode
    const lastPage = Math.max(1, Math.ceil(data.length / pageSize));
    if (currentPage > lastPage) {
      setPageUnified(lastPage);
    }
  }, [externallyPaginated, isPageControlled, data.length, pageSize, currentPage]);

  const handleItemClick = (val) => {
    if (val !== value) {
      onChange(val);
      setExpanded(false); // Close dropdown after selection
    }
  };

  const defaultRenderItem = (item) => {
    const obj = typeof item === 'string' ? { value: item, label: item } : item;
    const selected = obj.value === value;
    return (
      <Box
        key={obj.value}
        onClick={() => handleItemClick(obj.value)}
        role="option"
        aria-selected={selected}
        data-selected={selected || undefined}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleItemClick(obj.value); } }}
        className={`paginate-select-item${selected ? ' selected' : ''}`}
      >
        {obj.label}
      </Box>
    );
  };

  const [expanded, setExpanded] = React.useState(false);
  const inputRef = React.useRef();

  // Handlers for expand/collapse
  const handleInputFocus = () => {
    if (expandOnSelect) setExpanded(true);
  };
  const handleInputBlur = (e) => {
    if (expandOnSelect) {
      // Collapse only if focus moves outside the component
      if (!e.currentTarget.contains(e.relatedTarget)) setExpanded(false);
    }
    // On blur, restore input to selected label or empty
    if (onSearchChange) {
        onSearchChange(''); // Clear search value so input is empty
    }
  };

  // Find selected label for display
  const selectedObj = useMemo(() => {
    if (!value) return null;
    return data.find((d) => (typeof d === 'string' ? d === value : d.value === value));
  }, [data, value]);

  // Show search value only while expanded (typing/focused), else show selected label or empty
  const inputValue = (expanded && controlledSearch !== undefined && controlledSearch !== '')
    ? controlledSearch
    : (selectedObj ? (typeof selectedObj === 'string' ? selectedObj : selectedObj.label) : '');
  const showClear = !!selectedObj;

  // Custom onChange: typing only updates search, does not clear selection
  const handleInputChange = (e) => {
    const val = e.currentTarget.value;
    if (onSearchChange) onSearchChange(val);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) onChange(null);
    if (onSearchChange) onSearchChange('');
    setExpanded(false);
  };

  return (
    <Box
      // p="md"
      style={{

        position: 'relative', // for overlay
        ...style,
      }}
      tabIndex={expandOnSelect ? 0 : undefined}
      onBlur={handleInputBlur}
      {...rest}
    >
      <LoadingOverlay visible={!!(paginationProps && paginationProps.loading)} zIndex={1000} overlayBlur={2} />
      <Stack gap="md">
        {searchable && (
          <TextInput
            ref={inputRef}
            value={inputValue}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
            placeholder={placeholder}
            size={size}
            radius={radius}
            rightSection={showClear ? (
              <button
                type="button"
                aria-label="Clear selection"
                onClick={handleClear}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              >
                ×
              </button>
            ) : null}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            // Allow typing/search even when an option is selected
            {...selectProps}
          />
        )}
        {(!expandOnSelect || (
          <div
            className={`paginate-select-expandable${expanded ? ' expanded' : ''}`}
            aria-hidden={!expanded}
            style={{ overflow: 'hidden', transition: 'max-height 0.3s cubic-bezier(.4,0,.2,1), opacity 0.3s cubic-bezier(.4,0,.2,1), transform 0.3s cubic-bezier(.4,0,.2,1)', maxHeight: expanded ? 800 : 0, opacity: expanded ? 1 : 0, transform: expanded ? 'scaleY(1)' : 'scaleY(0.95)' }}
          >
            <ScrollArea h={listHeight} offsetScrollbars type="always" scrollbarSize={8}>
              {currentPageItems && currentPageItems.length ? (
                <div
                  className={`paginate-select-list paginate-select-${theme}`}
                  role="listbox"
                >
                  {currentPageItems.map((item) => (
                    renderItem ? renderItem({
                      item,
                      selected: (typeof item === 'string' ? item : item.value) === value,
                      onSelect: () => handleItemClick(typeof item === 'string' ? item : item.value),
                    }) : (
                      defaultRenderItem(item)
                    )
                  ))}
                </div>
              ) : (
                <Box style={{ fontSize: 12, color: 'var(--mantine-color-dimmed, #868e96)' }}>{nothingFound}</Box>
              )}
            </ScrollArea>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center',paddingTop:'10px' }}>
              <Pagination
                total={effectiveTotal}
                value={currentPage}
                onChange={setPageUnified}
                size={size}
                radius={radius}
                fullWidth
                {...paginationProps}
              />
            </div>
          </div>
        ))}
      </Stack>
    </Box>
  );
};

PaginateSelect.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
    ])
  ),
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  searchable: PropTypes.bool,
  nothingFound: PropTypes.string,
  radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shadow: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  withAsterisk: PropTypes.bool,
  page: PropTypes.number,
  defaultPage: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  size: PropTypes.string,
  selectProps: PropTypes.object,
  paginationProps: PropTypes.object,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  style: PropTypes.object,
  pageSize: PropTypes.number,
  listHeight: PropTypes.number,
  renderItem: PropTypes.func,
  colorScheme: PropTypes.oneOf(['light', 'dark']),
  theme: PropTypes.oneOf(['light', 'dark']),
  expandOnSelect: PropTypes.bool,
};

export default PaginateSelect;
